import RateByAddress from '@/components/tax-rate/rate-by-address';
import { Button } from '@/components/ui/button';
import AppLayout from '@/layouts/app-layout';
import { type SharedData } from '@/types';
import { TaxRate } from '@/types/TaxRate/taxRate';
import { Head, Link, router, usePage } from '@inertiajs/react';

export default function Welcome({ taxRate }: { taxRate: null | TaxRate }) {
    const { auth, name } = usePage<SharedData>().props;

    return (
        <>
            <AppLayout>
                <Head title={'Sales Tax Rates & Get Alerts | ' + name}></Head>
                <div className="flex h-full flex-1 flex-col gap-4 rounded-xl p-4">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Stay Ahead of Sales Tax Changes</h1>
                    <p className="mt-4 text-lg text-gray-700 dark:text-white">
                        Sales tax rates change frequently, impacting businesses and shoppers alike. Stop wasting time checking for updates—
                        <strong>we’ll keep you informed effortlessly.</strong>
                    </p>
                    <p className="mt-2 text-lg text-gray-700 dark:text-white">
                        With <span className="font-semibold">{name}</span>, you’ll always know when tax rates change in your saved locations. Whether
                        you're a business tracking multiple areas or a shopper avoiding surprises at checkout, we’ve got you covered.
                    </p>

                    <div className="mt-6 text-left">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">How It Works</h2>

                        <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">✔ For Registered Users</h3>
                        <p className="mt-2 text-lg text-gray-700 dark:text-white">
                            <strong>Create an Account:</strong>{' '}
                            <strong>
                                {' '}
                                <Link href={route('register')} className="underline">
                                    Sign up
                                </Link>{' '}
                            </strong>{' '}
                            in seconds to start tracking tax rates.
                        </p>
                        <p className="mt-1 text-lg text-gray-700 dark:text-white">
                            <strong>Add Your Locations:</strong> Save multiple addresses for automated monitoring.
                        </p>
                        <p className="mt-1 text-lg text-gray-700 dark:text-white">
                            <strong>Get Instant Alerts:</strong> Receive email notifications the moment tax rates change.
                        </p>
                        <p className="mt-1 text-lg text-gray-700 dark:text-white">
                            <strong>View Live Updates:</strong> Access real-time tax rates anytime in your dashboard.
                        </p>

                        <div className="col-span-full flex justify-center">
                            {auth.user ? (
                                <Button
                                    onClick={() => {
                                        router.visit(route('dashboard'));
                                    }}
                                    className="mt-2 w-full cursor-pointer rounded-xl bg-gray-200 px-6 py-3 font-semibold text-gray-900 shadow-md transition hover:bg-gray-300"
                                >
                                    Manage Locations
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => {
                                        router.visit(route('register'));
                                    }}
                                    className="mt-2 w-full cursor-pointer rounded-xl bg-gray-200 px-6 py-3 font-semibold text-gray-900 shadow-md transition hover:bg-gray-300"
                                >
                                    Sign Up & Stay Notified
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="mt-8 text-left">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">🔹 For Guest Users</h3>
                        <p className="mt-2 text-lg text-gray-700 dark:text-white">
                            <strong>Check Your Tax Rate:</strong> Instantly view the latest tax rate for any location.
                        </p>
                        <p className="mt-1 text-lg text-gray-700 dark:text-white">
                            <strong>Quick & Easy:</strong> No registration required—just enter an address to check rates.
                        </p>
                        <p className="mt-1 text-lg text-gray-700 dark:text-white">
                            <strong>Want Automatic Alerts?</strong>{' '}
                            <strong>
                                {' '}
                                <Link href={route('register')} className="underline">
                                    Sign up
                                </Link>{' '}
                            </strong>{' '}
                            to save locations and get notified when rates change.
                        </p>

                        <div className="mt-5 dark:text-white">
                            <RateByAddress rate={taxRate} />
                        </div>
                    </div>

                    <div className="hidden h-14.5 lg:block"></div>
                </div>
            </AppLayout>
        </>
    );
}
